import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api';

interface TemplatesSidebarProps {
  isCollapsed?: boolean;
}

const TemplatesSidebar: React.FC<TemplatesSidebarProps> = ({ isCollapsed = false }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState<any[]>([]);
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('search') || '');
  const selectedCategoryId = searchParams.get('category_id');
  const selectedTemplateType = searchParams.get('template_type');

  useEffect(() => {
    loadSidebarData();
  }, []);

  const loadSidebarData = async () => {
    setLoading(true);
    try {
      const [cats, counts, total] = await Promise.all([
        apiService.getCategories(),
        apiService.getTemplateCountByCategory(),
        apiService.getTemplateCount()
      ]);
      
      setCategories(cats);
      setCategoryCounts(counts);
      setTotalCount(total.count);
    } catch (err: any) {
      console.error('Error loading sidebar data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (categoryId: number | null) => {
    const params = new URLSearchParams(searchParams);
    if (categoryId) {
      params.set('category_id', categoryId.toString());
    } else {
      params.delete('category_id');
    }
    setSearchParams(params);
  };

  const handleTemplateTypeClick = (templateType: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (templateType) {
      params.set('template_type', templateType);
    } else {
      params.delete('template_type');
    }
    setSearchParams(params);
  };

  const getIconClass = (iconClass: string | null | undefined) => {
    if (!iconClass) return 'fa-layer-group';
    const parts = iconClass.split(' ');
    return parts.find(p => p.startsWith('fa-')) || 'fa-layer-group';
  };

  if (isCollapsed) {
    return (
      <>
        <div className="px-2 py-4 space-y-2">
          <button className="w-full flex items-center justify-center p-2 text-gray-600 hover:bg-gray-100 rounded-lg" title="Search">
            <i className="fa-solid fa-magnifying-glass text-sm"></i>
          </button>
        </div>
        <div className="px-2 py-2 border-t border-gray-200 space-y-1">
          <button
            onClick={() => handleCategoryClick(null)}
            className={`w-full flex items-center justify-center p-2 rounded-lg ${
              !selectedCategoryId ? 'text-violet-600 bg-violet-50' : 'text-gray-600 hover:bg-gray-100'
            }`}
            title="All Templates"
          >
            <i className="fa-solid fa-layer-group text-sm"></i>
          </button>
          {categories
            .filter((cat) => {
              const count = categoryCounts[cat.id] || 0;
              return count > 0;
            })
            .map((cat) => {
              const iconClass = getIconClass(cat.icon_class);
              const isSelected = selectedCategoryId === cat.id.toString();
              return (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryClick(cat.id)}
                  className={`w-full flex items-center justify-center p-2 rounded-lg ${
                    isSelected ? 'text-violet-600 bg-violet-50' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title={cat.name}
                >
                  <i className={`fa-solid ${iconClass} text-sm`}></i>
                </button>
              );
            })}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="px-4 pt-6 pb-4">
        <div className="relative">
          <i className="fa-solid fa-magnifying-glass absolute left-3 top-3 text-gray-400 text-sm"></i>
          <input 
            type="text" 
            placeholder="Search templates..." 
            value={searchQuery}
            onChange={(e) => {
              const value = e.target.value;
              setSearchQuery(value);
              
              // Update URL search params
              const params = new URLSearchParams(searchParams);
              if (value.trim()) {
                params.set('search', value.trim());
              } else {
                params.delete('search');
              }
              setSearchParams(params);
            }}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-violet-500" 
          />
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery('');
                const params = new URLSearchParams(searchParams);
                params.delete('search');
                setSearchParams(params);
              }}
              className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
            >
              <i className="fa-solid fa-times text-sm"></i>
            </button>
          )}
        </div>
      </div>
      <div className="px-4 py-2 border-t border-gray-200">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Type Document</span>
        <ul className="mt-2 space-y-1">
          <li>
            <button
              onClick={() => handleTemplateTypeClick(null)}
              className={`w-full flex items-center px-3 py-2 text-sm rounded-lg ${
                !selectedTemplateType
                  ? 'bg-violet-50 text-violet-600 font-medium'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <i className={`fa-solid fa-layer-group mr-3 text-sm ${
                !selectedTemplateType ? 'text-violet-600' : 'text-gray-500'
              }`}></i>
              <span className={!selectedTemplateType ? 'font-medium' : ''}>All Types</span>
            </button>
          </li>
          <li>
            <button
              onClick={() => handleTemplateTypeClick('Internal')}
              className={`w-full flex items-center px-3 py-2 text-sm rounded-lg ${
                selectedTemplateType === 'Internal'
                  ? 'bg-violet-50 text-violet-600 font-medium'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <i className={`fa-solid fa-building mr-3 text-sm ${
                selectedTemplateType === 'Internal' ? 'text-violet-600' : 'text-gray-500'
              }`}></i>
              <span className={selectedTemplateType === 'Internal' ? 'font-medium' : ''}>Internal</span>
            </button>
          </li>
          <li>
            <button
              onClick={() => handleTemplateTypeClick('External')}
              className={`w-full flex items-center px-3 py-2 text-sm rounded-lg ${
                selectedTemplateType === 'External'
                  ? 'bg-violet-50 text-violet-600 font-medium'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <i className={`fa-solid fa-handshake mr-3 text-sm ${
                selectedTemplateType === 'External' ? 'text-violet-600' : 'text-gray-500'
              }`}></i>
              <span className={selectedTemplateType === 'External' ? 'font-medium' : ''}>External</span>
            </button>
          </li>
        </ul>
      </div>
      <div className="px-4 py-2 border-t border-gray-200">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Categories</span>
        {loading ? (
          <div className="mt-2 flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-violet-600"></div>
          </div>
        ) : (
          <ul className="mt-2 space-y-1">
            <li>
              <button
                onClick={() => handleCategoryClick(null)}
                className={`w-full flex items-center px-3 py-2 text-sm rounded-lg ${
                  !selectedCategoryId
                    ? 'bg-violet-50 text-violet-600 font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <i className={`fa-solid fa-layer-group mr-3 text-sm ${
                  !selectedCategoryId ? 'text-violet-600' : 'text-gray-500'
                }`}></i>
                <span className={!selectedCategoryId ? 'font-medium' : ''}>All Templates</span>
                <span className={`ml-auto text-xs ${
                  !selectedCategoryId ? 'text-violet-600' : 'text-gray-500'
                }`}>
                  {totalCount}
                </span>
              </button>
            </li>
            {categories
              .filter((cat) => {
                const count = categoryCounts[cat.id] || 0;
                return count > 0;
              })
              .map((cat) => {
                const count = categoryCounts[cat.id] || 0;
                const isSelected = selectedCategoryId === cat.id.toString();
                const iconClass = getIconClass(cat.icon_class);
                
                return (
                  <li key={cat.id}>
                    <button
                      onClick={() => handleCategoryClick(cat.id)}
                      className={`w-full flex items-center px-3 py-2 text-sm rounded-lg ${
                        isSelected
                          ? 'bg-violet-50 text-violet-600 font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <i className={`fa-solid ${iconClass} mr-3 text-sm ${
                        isSelected ? 'text-violet-600' : 'text-gray-500'
                      }`}></i>
                      <span className={isSelected ? 'font-medium' : ''}>{cat.name}</span>
                      <span className={`ml-auto text-xs ${
                        isSelected ? 'text-violet-600' : 'text-gray-500'
                      }`}>
                        {count}
                      </span>
                    </button>
                  </li>
                );
              })}
          </ul>
        )}
      </div>
    </>
  );
};

export default TemplatesSidebar;

